/**
 * Which per-template config table a service template maps to.
 * - cdn: flat bandwidth-per-configoption table (SwiftFederation.CDN)
 * - vcloud: hierarchical resource table (Cloud.VCloud)
 * - conversant: shared Conversant table parameterised by metric label (Transcoding/Recording/etc.)
 * - vps: Cloud.VPS resource table (cpu/memory/disk/bandwidth/ip)
 * - hosting: Cloud.Hosting resource table (storage/website/mysql)
 * - colocation: generic table + rack-U assignment grid (Server.Colocation)
 * - baremetal: Server.BareMetalServer device table (brand/model conditional)
 * - email: Email.EGPlatform + Email.Server mailbox/account table
 * - ssl: SSL.SSL certificate info card (no configoptions)
 * Everything else falls back to the generic config table.
 */
export type ConfigTemplateKind =
  | 'cdn'
  | 'vcloud'
  | 'conversant'
  | 'vps'
  | 'hosting'
  | 'colocation'
  | 'baremetal'
  | 'email'
  | 'ssl';

/** Per-Conversant config: metric column header + which field to use as entity name. */
export interface ConversantTemplateConfig {
  /** Label for the "metric type" column (second column). */
  metricLabel: string;
  /** 'name' for Transcoding/Recording/Transmuxing; 'service_name' for Storage variants. */
  entityField: 'name' | 'service_name';
}

const CONVERSANT_CONFIGS: Record<string, ConversantTemplateConfig> = {
  'SwiftFederation.Transcoding': { metricLabel: 'Transcoding', entityField: 'name' },
  'SwiftFederation.Recording':   { metricLabel: 'Recording',   entityField: 'name' },
  'SwiftFederation.Transmuxing': { metricLabel: 'Transmuxing', entityField: 'name' },
  'SwiftFederation.StorageLFD':  { metricLabel: 'Storage LFD', entityField: 'service_name' },
  'SwiftFederation.StorageOSS':  { metricLabel: 'OSS',         entityField: 'service_name' },
};

export function configTemplateKind(template: string): ConfigTemplateKind | null {
  if (template.endsWith('.CDN')) return 'cdn';
  if (template === 'Cloud.VCloud') return 'vcloud';
  if (template in CONVERSANT_CONFIGS) return 'conversant';
  if (template === 'Cloud.VPS') return 'vps';
  if (template === 'Cloud.Hosting') return 'hosting';
  if (template === 'Server.Colocation') return 'colocation';
  if (template === 'Server.BareMetalServer') return 'baremetal';
  if (template === 'Email.EGPlatform' || template === 'Email.Server') return 'email';
  if (template === 'SSL.SSL') return 'ssl';
  return null;
}

export function conversantConfig(template: string): ConversantTemplateConfig | null {
  return CONVERSANT_CONFIGS[template] ?? null;
}
